import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  ApiV2InfiniteQueryOptions,
  apiV2Request,
  getDefaultInfiniteQueryOptionsV2,
} from "../apiV2Request";
import {
  PayoutListRequestType,
  PayoutListResponseType,
  PayoutListWithCountResponseType,
} from "@sellar-io/api-v2/types";
import { ServerStateKeys } from "../ServerStateKeys";

export const usePayoutList = <TData = PayoutListResponseType,>(
  params: PayoutListRequestType,
  options?: ApiV2InfiniteQueryOptions<TData>,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useInfiniteQuery({
    queryKey: [user?.uid, business?.id, ServerStateKeys.Payouts, params.search],
    queryFn: ({ pageParam }) =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: "payout/list",
        params: {
          ...params,
          offset: pageParam,
        },
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    ...getDefaultInfiniteQueryOptionsV2({
      context: { scope: "business", user, business: business },
      options: { ...options, limit: params.limit },
    }),
  });
};

export const usePayoutPaginatedList = <
  TData = PayoutListWithCountResponseType,
>(
  params: PayoutListRequestType,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useQuery<TData>({
    queryKey: [user?.uid, business?.id, ServerStateKeys.Payouts, params],
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: "payout/list-with-count",
        params,
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    placeholderData: (prev) => prev,
  });
};
